import React, { createRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useSnackbar } from 'notistack'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { TabContext } from '@mui/lab'
import { Tabs, Tab, Tooltip } from '@mui/material'

import { checkErrorResponse, Logger, LoggerTypes } from '../../../utils'
import {
  // -- actions: SCENARIOS
  importScenarioGlobals,
  // -- actions: VARIABLES
  createScenarioVariable,
  updateScenarioVariable,
  deleteScenarioVariable,
  searchScenarioVariables,
  // -- actions: RESOURCES
  createScenarioResource,
  updateScenarioResource,
  deleteScenarioResource,
  searchScenarioResources,
  // -- actions: PRODUCTS
  createScenarioProduct,
  updateScenarioProduct,
  deleteScenarioProduct,
  searchScenarioProducts,
  // -- actions: LOCATIONS
  // createScenarioLocation,
  // updateScenarioLocation,
  // deleteScenarioLocation,
  searchScenarioLocations,
  // -- actions: WEATHERS
  // createScenarioWeather,
  // updateScenarioWeather,
  // deleteScenarioWeather, // <-- weather only apply to project
  // deleteProjectWeather,
  searchScenarioWeathers,
  // -- actions: PARAMETERS
  createScenarioParameter,
  updateScenarioParameter,
  deleteScenarioParameter,
  searchScenarioParameters
} from '../../../actions'
import {
  iconNames,
  Icon,
  AttributesTabPanel,
  AttributeResourcePanel,
  AttributeProductPanel,
  AttributeVariablePanel,
  AttributeLocationPanel,
  AttributeParameterPanel,
  AttributeWeatherPanel,
  AccessControl,
  AccessControlNoRights
} from '../../../components'
import { attributesNames, userRightTypes } from '../../../constants'
import { GlobalAttributeTabsDialog } from '../../globalAttributes'
import './style.scss'

export const ScenarioAttributesTabs = ({
  currentProject,
  currentScenario,
  loadData,
  variables,
  resources,
  products,
  locations,
  weather_data,
  parameters
}) => {
  const { t } = useTranslation()
  const ref = createRef()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const [tabValue, setTabValue] = useState(0)

  const [openDialogVariable, setOpenDialogVariable] = useState(false)
  const [openDialogResource, setOpenDialogResource] = useState(false)
  const [openDialogProduct, setOpenDialogProduct] = useState(false)
  const [openDialogLocation, setOpenDialogLocation] = useState(false)
  const [openDialogParameter, setOpenDialogParameter] = useState(false)
  const [openDialogWeather, setOpenDialogWeather] = useState(false)

  const [searchTermVariables, setSearchTermVariables] = useState('')
  const [searchTermResources, setSearchTermResources] = useState('')
  const [searchTermProducts, setSearchTermProducts] = useState('')
  const [searchTermLocations, setSearchTermLocations] = useState('')
  const [searchTermParameters, setSearchTermParameters] = useState('')
  const [searchTermWeathers, setSearchTermWeathers] = useState('')

  const [openImportDialog, setOpenImportDialog] = useState(false)

  const onTabChange = (event, newValue) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioAttributesTabs.onTabChange()', newValue)
    setTabValue(newValue)
  }

  const _dispatchAction = ({ func, funcName }) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioAttributesTabs._dispatchAction()', { funcName })

    dispatch(func)
      .then((res) => {
        Logger.trace(LoggerTypes.CONTROLLER, `ScenarioAttributesTabs._dispatchAction().dispatch(${funcName}).then()`, {
          res
        })
        if (res?.data?.isAxiosError) {
          enqueueSnackbar(t('common.api_failure'), { variant: 'error' })
        }
      })
      .catch((res) => {
        Logger.trace(LoggerTypes.WARN, `ScenarioAttributesTabs._dispatchAction().dispatch(${funcName}).catch()`, {
          res
        })
        checkErrorResponse(res, enqueueSnackbar, t)
      })
  }

  // == RESOURCES
  const onScenarioResourceDelete = (resourceId, _projectId, _scenarioId) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioAttributesTabs.onScenarioResourceDelete()', {
      _projectId,
      _scenarioId,
      resourceId
    })
    _dispatchAction({
      func: deleteScenarioResource(resourceId, _projectId, _scenarioId),
      funcName: 'deleteScenarioResource'
    })
  }
  const onScenarioResourceAdd = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioAttributesTabs.onScenarioResourceAdd()')
    setOpenDialogResource(true)
  }
  const onScenarioResourceSearch = (term) => {
    setSearchTermResources(term.target.value)
    dispatch(searchScenarioResources(term.target.value))
  }

  // == PRODUCTS
  const onScenarioProductDelete = (productId, _projectId, _scenarioId) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioAttributesTabs.onScenarioProductDelete()', {
      _projectId,
      _scenarioId,
      productId
    })
    _dispatchAction({
      func: deleteScenarioProduct(productId, _projectId, _scenarioId),
      funcName: 'deleteScenarioProduct'
    })
  }
  const onScenarioProductAdd = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioAttributesTabs.onScenarioProductAdd()')
    setOpenDialogProduct(!openDialogProduct)
  }
  const onScenarioProductSearch = (term) => {
    setSearchTermProducts(term.target.value)
    dispatch(searchScenarioProducts(term.target.value))
  }

  // == VARIABLES
  const onScenarioVariableDelete = (variableId, _projectId, _scenarioId) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioAttributesTabs.onDeleteScenarioVariable()', {
      _projectId,
      _scenarioId,
      variableId
    })
    _dispatchAction({
      func: deleteScenarioVariable(variableId, _projectId, _scenarioId),
      funcName: 'deleteScenarioVariable'
    })
  }
  const onScenarioVariableAdd = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioAttributesTabs.onVariablesDialogOpen()')
    setOpenDialogVariable(true)
  }
  const onScenarioVariableSearch = (term) => {
    setSearchTermVariables(term.target.value)
    dispatch(searchScenarioVariables(term.target.value))
  }

  // == LOCATIONS
  // const onScenarioLocationDelete = (locationId, _projectId, _scenarioId) => {
  //   Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioAttributesTabs.onScenarioLocationDelete()', {
  //     _projectId,
  //     _scenarioId,
  //     locationId
  //   })
  //   _dispatchAction({
  //     func: deleteScenarioLocation(locationId, _projectId, _scenarioId),
  //     funcName: 'deleteScenarioLocation'
  //   })
  // }
  // const onScenarioLocationAdd = () => {
  //   Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioAttributesTabs.onScenarioLocationAdd()')
  //   setOpenDialogLocation(!openDialogLocation)
  // }
  const onScenarioLocationSearch = (term) => {
    setSearchTermLocations(term.target.value)
    dispatch(searchScenarioLocations(term.target.value))
  }

  // == WEATHERS
  // == Weather
  // const onScenarioWeatherDelete = (weatherId, _projectId, _scenarioId) => {
  //   Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioAttributesTabs.onScenarioWeatherDelete()', {
  //     _projectId,
  //     _scenarioId,
  //     weatherId
  //   })
  //   _dispatchAction({
  //     func: deleteProjectWeather(weatherId, _projectId),
  //     funcName: 'deleteProjectWeather'
  //   })
  // }
  // const onScenarioWeatherAdd = () => {
  //   Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioAttributesTabs.onScenarioWeatherAdd()')
  //   setOpenDialogWeather(!openDialogWeather)
  // }
  const onScenarioWeatherSearch = (term) => {
    setSearchTermWeathers(term.target.value)
    dispatch(searchScenarioWeathers(term.target.value))
  }

  // == PARAMETERS
  const onScenarioParameterDelete = (parameterId, _projectId, _scenarioId) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioAttributesTabs.onScenarioParameterDelete()', {
      _projectId,
      _scenarioId,
      parameterId
    })
    _dispatchAction({
      func: deleteScenarioParameter(parameterId, _projectId, _scenarioId),
      funcName: 'deleteScenarioParameter'
    })
  }
  const onScenarioParameterAdd = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioAttributesTabs.onScenarioParameterAdd()')
    setOpenDialogParameter(!openDialogParameter)
  }
  const onScenarioParameterSearch = (term) => {
    setSearchTermParameters(term.target.value)
    dispatch(searchScenarioParameters(term.target.value))
  }

  // == IMPORT
  const onScenarioAttributeImportOpen = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioAttributesTabs.onScenarioAttributeImportOpen()', { openImportDialog })
    setOpenImportDialog(true)
  }

  // Logger.trace(LoggerTypes.CONTAINER, 'ScenarioAttributesTabs.render()', {
  //   currentProject,
  //   currentScenario,
  //   attributes: {
  //     variables,
  //     resources,
  //     products,
  //     locations,
  //     weather_data,
  //     parameters
  //   }
  // })

  return (
    <div className="ScenarioAttributesTabs">
      <AccessControl
        userPermissions={[userRightTypes.PROJECT_ATTRIBUTES_READ]}
        renderNoAccess={() => <AccessControlNoRights />}
      >
        <TabContext value={`${tabValue}`}>
          <Tabs aria-label="library selector" centered={true} value={tabValue} onChange={onTabChange}>
            <Tooltip ref={ref} title={t('attributes.resources.tab_title')}>
              <Tab disableRipple={true} id={`simple-tab-${0}`} icon={<Icon name={iconNames.RESOURCE} size={20} />} />
            </Tooltip>
            <Tooltip ref={ref} title={t('attributes.products.tab_title')}>
              <Tab disableRipple={true} id={`simple-tab-${1}`} icon={<Icon name={iconNames.PRODUCT} size={20} />} />
            </Tooltip>
            <Tooltip ref={ref} title={t('attributes.variables.tab_title')}>
              <Tab disableRipple={true} id={`simple-tab-${2}`} icon={<Icon name={iconNames.VARIABLE} size={20} />} />
            </Tooltip>
            <Tooltip ref={ref} title={t('attributes.parameters.tab_title')}>
              <Tab disableRipple={true} id={`simple-tab-${3}`} icon={<Icon name={iconNames.PARAMETER} size={18} />} />
            </Tooltip>
            <Tooltip ref={ref} title={t('attributes.weather_data.tab_title')}>
              <Tab
                disableRipple={true}
                id={`simple-tab-${4}`}
                icon={<Icon name={iconNames.WEATHER_DATA} size={22} />}
              />
            </Tooltip>
            <Tooltip ref={ref} title={t('attributes.locations.tab_title')}>
              <Tab disableRipple={true} id={`simple-tab-${5}`} icon={<Icon name={iconNames.LOCATION} size={18} />} />
            </Tooltip>
          </Tabs>

          {/* TAB - RESOURCES */}
          <AttributesTabPanel value={tabValue} index={0}>
            <AttributeResourcePanel
              currentProject={currentProject}
              currentScenario={currentScenario}
              resources={resources}
              parameters={parameters}
              terms={searchTermResources}
              openDialog={openDialogResource}
              setOpenDialog={setOpenDialogResource}
              onSearch={onScenarioResourceSearch}
              onAdd={onScenarioResourceAdd}
              onDelete={onScenarioResourceDelete}
              onCreate={createScenarioResource}
              onUpdate={updateScenarioResource}
              onImport={onScenarioAttributeImportOpen}
            />
          </AttributesTabPanel>

          {/* TAB - PRODUCTS */}
          <AttributesTabPanel value={tabValue} index={1}>
            <AttributeProductPanel
              currentProject={currentProject}
              currentScenario={currentScenario}
              products={products}
              terms={searchTermProducts}
              openDialog={openDialogProduct}
              setOpenDialog={setOpenDialogProduct}
              onSearch={onScenarioProductSearch}
              onAdd={onScenarioProductAdd}
              onDelete={onScenarioProductDelete}
              onCreate={createScenarioProduct}
              onUpdate={updateScenarioProduct}
              onImport={onScenarioAttributeImportOpen}
            />
          </AttributesTabPanel>

          {/* TAB - VARIABLES */}
          <AttributesTabPanel value={tabValue} index={2}>
            <AttributeVariablePanel
              currentProject={currentProject}
              currentScenario={currentScenario}
              variables={variables}
              terms={searchTermVariables}
              openDialog={openDialogVariable}
              setOpenDialog={setOpenDialogVariable}
              onSearch={onScenarioVariableSearch}
              onAdd={onScenarioVariableAdd}
              onDelete={onScenarioVariableDelete}
              onCreate={createScenarioVariable}
              onUpdate={updateScenarioVariable}
              onImport={onScenarioAttributeImportOpen}
            />
          </AttributesTabPanel>

          {/* TAB - PARAMETERS */}
          <AttributesTabPanel value={tabValue} index={3}>
            <AttributeParameterPanel
              currentProject={currentProject}
              currentScenario={currentScenario}
              parameters={parameters}
              terms={searchTermParameters}
              openDialog={openDialogParameter}
              setOpenDialog={setOpenDialogParameter}
              onSearch={onScenarioParameterSearch}
              onAdd={onScenarioParameterAdd}
              onDelete={onScenarioParameterDelete}
              onCreate={createScenarioParameter}
              onUpdate={updateScenarioParameter}
              onImport={onScenarioAttributeImportOpen}
            />
          </AttributesTabPanel>

          {/* TAB - WEATHERS */}
          <AttributesTabPanel value={tabValue} index={4}>
            <AttributeWeatherPanel
              currentProject={currentProject}
              currentScenario={currentScenario}
              weather_data={weather_data}
              terms={searchTermWeathers}
              openDialog={openDialogWeather}
              setOpenDialog={setOpenDialogWeather}
              onSearch={onScenarioWeatherSearch}
              // onAdd={onScenarioWeatherAdd}
              // onDelete={onScenarioWeatherDelete}
              // onCreate={createScenarioWeather}
              // onUpdate={updateScenarioWeather}
              // onImport={onScenarioAttributeImportOpen}
            />
          </AttributesTabPanel>

          {/* TAB - LOCATIONS */}
          <AttributesTabPanel value={tabValue} index={5}>
            <AttributeLocationPanel
              currentProject={currentProject}
              currentScenario={currentScenario}
              locations={locations}
              terms={searchTermLocations}
              openDialog={openDialogLocation}
              setOpenDialog={setOpenDialogLocation}
              onSearch={onScenarioLocationSearch}
              // onAdd={onScenarioLocationAdd}
              // onDelete={onScenarioLocationDelete}
              // onCreate={createScenarioLocation}
              // onUpdate={updateScenarioLocation}
              // onImport={onScenarioAttributeImportOpen}
            />
          </AttributesTabPanel>

          {openImportDialog && (
            <GlobalAttributeTabsDialog
              currentProject={currentProject}
              currentScenario={currentScenario}
              tabValue={tabValue}
              // -- MUST respect the tabs order
              tabIdentifier={[
                attributesNames.RESOURCES, // 0
                attributesNames.PRODUCTS, // 1
                attributesNames.VARIABLES, // 2
                attributesNames.PARAMETERS // 3
                // attributesNames.WEATHER_DATA, // 4
                // attributesNames.LOCATIONS // 5
              ]}
              setTabValue={setTabValue}
              openDialog={openImportDialog}
              setOpenDialog={setOpenImportDialog}
              actionDispatchImport={importScenarioGlobals}
              // -- MUST respect the order of appearance in the Global import popin
              tabVisible={{
                resources: true,
                products: true,
                variables: true,
                parameters: true,
                weather_data: false,
                locations: false
              }}
              // -- Object of array of objects that contain the selected items in the
              // -- Global import attribute popin
              selectedAttributes={{
                resources,
                products,
                variables,
                parameters
                // weather_data,
                // locations
              }}
              loadData={loadData}
            />
          )}
        </TabContext>
      </AccessControl>
    </div>
  )
}

ScenarioAttributesTabs.displayName = 'ScenarioAttributesTabs'

ScenarioAttributesTabs.propTypes = {
  currentProject: PropTypes.object,
  currentScenario: PropTypes.object,
  loadData: PropTypes.arrayOf(PropTypes.object).isRequired,
  variables: PropTypes.arrayOf(PropTypes.object),
  resources: PropTypes.arrayOf(PropTypes.object),
  products: PropTypes.arrayOf(PropTypes.object),
  locations: PropTypes.arrayOf(PropTypes.object),
  weather_data: PropTypes.arrayOf(PropTypes.object),
  parameters: PropTypes.arrayOf(PropTypes.object)
}

ScenarioAttributesTabs.defaultProps = {
  currentProject: null,
  currentScenario: null,
  loadData: [],
  variables: [],
  resources: [],
  products: [],
  locations: [],
  weather_data: [],
  parameters: []
}
